import React, { useState } from 'react';
import { Button } from './Button';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
interface Question {
  id: string;
  text: string;
  type: 'text' | 'scale' | 'choice' | 'multiselect';
  choices?: string[];
  imageUrl?: string;
}
interface QuestionnaireFlowProps {
  onComplete: (answers: Record<string, any>) => void;
  onBack: () => void;
}
const QuestionnaireFlow: React.FC<QuestionnaireFlowProps> = ({
  onComplete,
  onBack
}) => {
  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isComplete, setIsComplete] = useState(false);
  // Multiple question sets to rotate through
  const questionSets = [
  // Set 1: Focus on mental state and needs
  [{
    id: 'mood',
    text: 'Hoe zou je je algemene stemming van vandaag omschrijven?',
    type: 'choice',
    choices: ['Energiek en positief', 'Gespannen maar productief', 'Rustig en tevreden', 'Vermoeid of somber', 'Neutraal']
  }, {
    id: 'energy',
    text: 'Hoe is je energieniveau vandaag?',
    type: 'scale'
  }, {
    id: 'focus',
    text: 'Hoe goed kun je je concentreren vandaag?',
    type: 'scale'
  }, {
    id: 'needs',
    text: 'Waar heb je vandaag het meeste behoefte aan?',
    type: 'multiselect',
    choices: ['Ontspanning', 'Focus', 'Energie', 'Rust', 'Motivatie', 'Inzicht', 'Verbinding']
  }],
  // Set 2: Focus on stress and coping
  [{
    id: 'stress',
    text: 'Hoeveel stress ervaar je momenteel?',
    type: 'scale'
  }, {
    id: 'stressors',
    text: 'Waar komt je stress voornamelijk vandaan?',
    type: 'multiselect',
    choices: ['Werk of studie', 'Relaties', 'Gezondheid', 'Financiën', 'Toekomst', 'Dagelijkse verplichtingen', 'Anders']
  }, {
    id: 'coping',
    text: 'Welke copingstrategieën gebruik je momenteel?',
    type: 'multiselect',
    choices: ['Beweging', 'Meditatie', 'Praten met anderen', 'Creatieve activiteiten', 'Natuur opzoeken', 'Afleiding zoeken', 'Geen specifieke strategie']
  }, {
    id: 'support',
    text: 'Hoeveel steun ervaar je van je omgeving?',
    type: 'scale'
  }],
  // Set 3: Focus on sleep and physical wellbeing
  [{
    id: 'sleep',
    text: 'Hoe goed heb je de afgelopen nachten geslapen?',
    type: 'scale'
  }, {
    id: 'sleepIssues',
    text: 'Welke slaapproblemen ervaar je?',
    type: 'multiselect',
    choices: ['Moeite met inslapen', 'Vroeg wakker worden', 'Onrustige slaap', 'Te kort slapen', 'Nachtmerries', 'Geen problemen', 'Anders']
  }, {
    id: 'physical',
    text: 'Hoe voel je je lichamelijk?',
    type: 'choice',
    choices: ['Fit en energiek', 'Redelijk goed', 'Wat gespannen', 'Vermoeid', 'Pijnklachten']
  }, {
    id: 'exercise',
    text: 'Hoeveel beweeg je gemiddeld per dag?',
    type: 'choice',
    choices: ['Nauwelijks', '15-30 minuten', '30-60 minuten', 'Meer dan een uur', 'Intensieve training']
  }],
  // Set 4: Focus on relationships and social wellbeing
  [{
    id: 'relationships',
    text: 'Hoe zou je je relaties met anderen op dit moment omschrijven?',
    type: 'choice',
    choices: ['Zeer bevredigend', 'Goed maar kan beter', 'Wisselend', 'Gespannen', 'Eenzaam']
  }, {
    id: 'connection',
    text: 'Voel je je verbonden met de mensen om je heen?',
    type: 'scale'
  }, {
    id: 'socialNeeds',
    text: 'Waar heb je behoefte aan in je sociale leven?',
    type: 'multiselect',
    choices: ['Diepere gesprekken', 'Meer contact', 'Betere grenzen', 'Nieuwe ontmoetingen', 'Minder verplichtingen', 'Meer begrip', 'Tevreden zoals het is']
  }, {
    id: 'communication',
    text: 'Hoe ervaar je je communicatie met anderen?',
    type: 'choice',
    choices: ['Zeer effectief', 'Meestal goed', 'Soms lastig', 'Vaak misverstanden', 'Zeer moeizaam']
  }],
  // Set 5: Focus on work and purpose
  [{
    id: 'workSatisfaction',
    text: 'Hoe tevreden ben je met je huidige werk of dagbesteding?',
    type: 'scale'
  }, {
    id: 'workChallenges',
    text: 'Welke uitdagingen ervaar je in je werk?',
    type: 'multiselect',
    choices: ['Werkdruk', 'Onduidelijke verwachtingen', 'Conflicten', 'Gebrek aan erkenning', 'Saai of monotoon', 'Te weinig uitdaging', 'Te veel verantwoordelijkheid', 'Geen significante uitdagingen']
  }, {
    id: 'purpose',
    text: 'Ervaar je voldoende zingeving in je leven?',
    type: 'choice',
    choices: ['Ja, zeer betekenisvol', 'Meestal wel', 'Soms', 'Weinig', 'Helemaal niet']
  }, {
    id: 'balance',
    text: 'Hoe is je werk-privé balans?',
    type: 'choice',
    choices: ['Uitstekend', 'Goed', 'Redelijk', 'Onbalans', 'Zeer problematisch']
  }],
  // Set 6: Focus on personal growth and challenges
  [{
    id: 'selfImage',
    text: 'Hoe zou je je zelfbeeld omschrijven?',
    type: 'choice',
    choices: ['Zeer positief', 'Overwegend positief', 'Neutraal', 'Overwegend negatief', 'Zeer negatief']
  }, {
    id: 'perfectionism',
    text: 'In hoeverre herken je jezelf in perfectionistische neigingen?',
    type: 'scale'
  }, {
    id: 'personalChallenges',
    text: 'Met welke persoonlijke uitdagingen worstel je momenteel?',
    type: 'multiselect',
    choices: ['Onzekerheid', 'Perfectionisme', 'Piekeren', 'Uitstelgedrag', 'Moeite met grenzen', 'Negatieve gedachten', 'Verslaving', 'Angst voor afwijzing', 'Geen significante uitdagingen']
  }, {
    id: 'growthAreas',
    text: 'Op welke gebieden zou je graag willen groeien?',
    type: 'multiselect',
    choices: ['Zelfvertrouwen', 'Communicatie', 'Emotieregulatie', 'Relaties', 'Carrière', 'Creativiteit', 'Mentale weerbaarheid', 'Zelfkennis']
  }]];
  const currentQuestionSet = questionSets[currentSetIndex];
  const currentQuestion = currentQuestionSet[currentQuestionIndex];
  const totalQuestions = currentQuestionSet.length;
  const handleAnswer = (value: any) => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: value
    });
  };
  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setIsComplete(true);
    }
  };
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else {
      onBack();
    }
  };
  const handleComplete = () => {
    onComplete(answers);
  };
  const isMultiSelectOptionSelected = (option: string) => {
    const currentAnswers = answers[currentQuestion.id] || [];
    return currentAnswers.includes(option);
  };
  const handleMultiSelectOption = (option: string) => {
    const currentAnswers = answers[currentQuestion.id] || [];
    if (currentAnswers.includes(option)) {
      handleAnswer(currentAnswers.filter((item: string) => item !== option));
    } else {
      handleAnswer([...currentAnswers, option]);
    }
  };
  if (isComplete) {
    return <div className="w-full max-w-xl mx-auto py-8 px-4" data-id="element-994">
        <div className="p-8 border border-gray-200 rounded-xl text-center" data-id="element-995">
          <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-6" data-id="element-996">
            <CheckIcon className="w-8 h-8 text-gray-700" data-id="element-997" />
          </div>
          <h2 className="text-2xl font-light text-gray-800 mb-4" data-id="element-998">
            Bedankt voor je antwoorden
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-999">
            Op basis van je antwoorden hebben we enkele suggesties voor je
            samengesteld.
          </p>
          <Button onClick={handleComplete} data-id="element-1000">Bekijk suggesties</Button>
        </div>
      </div>;
  }
  return <div className="w-full max-w-xl mx-auto py-8 px-4" data-id="element-1001">
      <div className="mb-8 flex justify-between items-center" data-id="element-1002">
        <div className="flex space-x-1" data-id="element-1003">
          {Array.from({
          length: totalQuestions
        }).map((_, index) => <div key={index} className={`w-8 h-1 rounded-full ${index <= currentQuestionIndex ? 'bg-gray-800' : 'bg-gray-300'}`} data-id="element-1004"></div>)}
        </div>
        <span className="text-sm text-gray-500" data-id="element-1005">
          {currentQuestionIndex + 1}/{totalQuestions}
        </span>
      </div>
      <div className="mb-12" data-id="element-1006">
        <h2 className="text-2xl font-light text-gray-800 mb-8" data-id="element-1007">
          {currentQuestion.text}
        </h2>
        {currentQuestion.imageUrl && <div className="mb-6 rounded-lg overflow-hidden" data-id="element-1008">
            <img src={currentQuestion.imageUrl} alt="Vraag illustratie" className="w-full h-auto" data-id="element-1009" />
          </div>}
        {currentQuestion.type === 'text' && <textarea className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none" placeholder="Typ je antwoord hier..." value={answers[currentQuestion.id] || ''} onChange={e => handleAnswer(e.target.value)} data-id="element-1010"></textarea>}
        {currentQuestion.type === 'scale' && <div className="py-6" data-id="element-1011">
            <div className="flex justify-between items-center" data-id="element-1012">
              {[1, 2, 3, 4, 5, 6, 7].map(value => <button key={value} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-200 ${answers[currentQuestion.id] === value ? 'bg-gray-200 border-2 border-gray-400' : 'border-2 border-gray-300 hover:bg-gray-100'}`} onClick={() => handleAnswer(value)} data-id="element-1013">
                  <span className="text-lg text-gray-700" data-id="element-1014">{value}</span>
                </button>)}
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-600" data-id="element-1015">
              <span data-id="element-1016">Zeer laag</span>
              <span data-id="element-1017">Neutraal</span>
              <span data-id="element-1018">Zeer hoog</span>
            </div>
          </div>}
        {currentQuestion.type === 'choice' && currentQuestion.choices && <div className="space-y-3" data-id="element-1019">
            {currentQuestion.choices.map(choice => <button key={choice} className={`w-full p-4 text-left border rounded-lg transition-colors duration-200 ${answers[currentQuestion.id] === choice ? 'border-gray-400 bg-gray-100' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => handleAnswer(choice)} data-id="element-1020">
                {choice}
              </button>)}
          </div>}
        {currentQuestion.type === 'multiselect' && currentQuestion.choices && <div className="space-y-3" data-id="element-1021">
            {currentQuestion.choices.map(choice => <button key={choice} className={`w-full p-4 text-left border rounded-lg transition-colors duration-200 flex justify-between items-center ${isMultiSelectOptionSelected(choice) ? 'border-gray-400 bg-gray-100' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => handleMultiSelectOption(choice)} data-id="element-1022">
                <span data-id="element-1023">{choice}</span>
                {isMultiSelectOptionSelected(choice) && <CheckIcon className="w-5 h-5 text-gray-700" data-id="element-1024" />}
              </button>)}
          </div>}
      </div>
      <div className="flex justify-between" data-id="element-1025">
        <Button variant="secondary" onClick={handlePrevious} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1027" />} data-id="element-1026">
          {currentQuestionIndex === 0 ? 'Terug' : 'Vorige'}
        </Button>
        <Button onClick={handleNext} rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-1029" />} disabled={!answers[currentQuestion.id]} data-id="element-1028">
          {currentQuestionIndex < totalQuestions - 1 ? 'Volgende' : 'Afronden'}
        </Button>
      </div>
    </div>;
};
export default QuestionnaireFlow;